import { TRPCClient } from '../utils/apiClient';

export type TemplateSavedVersionEntry = {
  id: string;
  sequence: number;
  createdAt: string;
  persistedRevision: number;
  source: string | null;
  isBaseline: boolean;
  saveInvocationId: string;
};

export type TemplateSavedVersionListPage = {
  entries: TemplateSavedVersionEntry[];
  nextCursor: string | null;
};

export type TemplateSavedVersionPreview = {
  version: TemplateSavedVersionEntry;
  diffSummary: string[];
  restoreToken: string;
};

type SavedVersionsClient = {
  listSavedVersions: {
    query: (input: {
      templateId: string;
      limit?: number;
      cursor?: string;
    }) => Promise<TemplateSavedVersionListPage>;
  };
  previewSavedVersion: {
    mutate: (input: {
      templateId: string;
      savedVersionId: string;
      boundWorkingRevision: number;
      boundPersistedRevision: number;
      workingCopySummary?: Record<string, unknown> | null;
    }) => Promise<TemplateSavedVersionPreview>;
  };
  redeemSavedVersion: {
    mutate: (input: {
      templateId: string;
      restoreToken: string;
      expectedWorkingRevision: number;
      expectedPersistedRevision: number;
    }) => Promise<{ adTemplate: Record<string, unknown> }>;
  };
};

const OBJECT_ID_PATTERN = /^[0-9a-fA-F]{24}$/;

function client(): SavedVersionsClient {
  return TRPCClient.client.adTemplate as unknown as SavedVersionsClient;
}

export async function fetchTemplateSavedVersions(
  templateId: string,
  options: { limit?: number; cursor?: string } = {},
): Promise<TemplateSavedVersionListPage> {
  if (!OBJECT_ID_PATTERN.test(templateId)) {
    return { entries: [], nextCursor: null };
  }
  return client().listSavedVersions.query({
    templateId,
    limit: options.limit,
    cursor: options.cursor,
  });
}

export async function previewTemplateSavedVersion(
  templateId: string,
  savedVersionId: string,
  bindings: {
    boundWorkingRevision: number;
    boundPersistedRevision: number;
    workingCopySummary?: Record<string, unknown> | null;
  },
): Promise<TemplateSavedVersionPreview> {
  return client().previewSavedVersion.mutate({
    templateId,
    savedVersionId,
    boundWorkingRevision: bindings.boundWorkingRevision,
    boundPersistedRevision: bindings.boundPersistedRevision,
    workingCopySummary: bindings.workingCopySummary,
  });
}

export async function redeemTemplateSavedVersion(
  templateId: string,
  args: {
    restoreToken: string;
    expectedWorkingRevision: number;
    expectedPersistedRevision: number;
  },
): Promise<{ adTemplate: Record<string, unknown> }> {
  return client().redeemSavedVersion.mutate({
    templateId,
    ...args,
  });
}
